import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useReducedMotion } from 'framer-motion'
import { BellOff, MessageSquare, Mic, MicOff, Phone, PhoneOff } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { usePermissions } from '@/hooks/usePermission'
import { INBOX_PERMISSIONS } from '@/lib/permission-sets'
import { cn, getInitials } from '@/lib/utils'
import { useCallStore } from '@/stores/callStore'
import {
  acceptIncoming,
  hangUp,
  rejectIncoming,
  setCanAnswer,
  silenceRinging,
  toggleMute,
} from './call-engine'
import { formatCallClock, formatCallNumber } from './format'

const CALL_PERMS = [INBOX_PERMISSIONS.call]

/** Seconds since `since`, ticking once a second; re-renders only itself. */
function Elapsed({ since }: { since: number }) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [])
  return <span className="tabular-nums">{formatCallClock((now - since) / 1000)}</span>
}

/** Caller initials on Setu's brand orange (same as every contact avatar). */
function CallerAvatar({ name, className }: { name: string; className?: string }) {
  return (
    <span
      className={cn(
        'flex shrink-0 items-center justify-center rounded-full bg-brand font-semibold text-brand-foreground',
        className,
      )}
    >
      {getInitials(name) || '?'}
    </span>
  )
}

const ARCH = 'M6 34 C 6 4, 154 4, 154 34'

/**
 * Setu means "bridge": the caller on one bank, your number on the other, and
 * a signal crossing the span while the call rings. Drawn after the Setu
 * bridge mark (public/favicon-bridge.svg).
 */
function CallBridge({ moving }: { moving: boolean }) {
  return (
    <svg viewBox="0 0 160 40" className="h-10 w-full min-w-0" aria-hidden>
      <g fill="none" strokeLinecap="round" className="stroke-border">
        <path d={ARCH} strokeWidth="2" />
        <path d="M44 15.5V34M80 11V34M116 15.5V34" strokeWidth="1.5" />
        <path d="M0 34H160" strokeWidth="2" />
      </g>
      <circle r="3.5" className="fill-brand" cx={moving ? 0 : 80} cy={moving ? 0 : 11.5}>
        {moving && <animateMotion dur="1.8s" repeatCount="indefinite" path={ARCH} />}
      </circle>
    </svg>
  )
}

/** Your side of the bridge — the Setu mark in the app's primary colour. */
function BusinessEnd() {
  return (
    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
      <svg viewBox="0 0 48 48" className="h-6 w-6" fill="none" aria-hidden>
        <g stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
          <path d="M10.5 30.5C10.5 12 37.5 12 37.5 30.5" strokeWidth="3.4" />
          <path d="M17.5 19.6V30.5M24 16.7V30.5M30.5 19.6V30.5" strokeWidth="2.4" />
          <path d="M7.5 30.5H40.5M13 30.5V37M35 30.5V37" strokeWidth="3.4" />
        </g>
      </svg>
    </span>
  )
}

/**
 * App-wide WhatsApp call UI: the ringing card for an inbound call and the
 * bar for the call in progress. Mounted once in AppLayout so a call rings on
 * every page. Renders nothing when there is no call.
 */
export function CallOverlay() {
  const incoming = useCallStore((s) => s.incoming)
  const active = useCallStore((s) => s.active)
  const ringSilenced = useCallStore((s) => s.ringSilenced)
  const ringBlocked = useCallStore((s) => s.ringBlocked)
  const canCall = usePermissions(CALL_PERMS)
  const reduceMotion = useReducedMotion()
  const navigate = useNavigate()
  // When the current incoming call started ringing on this screen.
  const [ringingSince, setRingingSince] = useState(() => Date.now())

  useEffect(() => {
    setCanAnswer(canCall)
  }, [canCall])

  const incomingId = incoming?.id
  useEffect(() => {
    if (incomingId) setRingingSince(Date.now())
  }, [incomingId])

  if (!incoming && !active) return null

  const openChat = (customerId: number | null) => {
    if (customerId) navigate(`/inbox?contact=${customerId}`)
  }

  return (
    <div className="pointer-events-none fixed bottom-4 right-4 z-[60] flex w-[min(360px,calc(100vw-32px))] flex-col gap-3">
      {incoming && (
        <section
          role="alertdialog"
          data-call-card
          aria-label={`Incoming WhatsApp call from ${incoming.customerName}`}
          className="pointer-events-auto rounded-2xl border bg-card p-4 text-card-foreground shadow-[0_24px_48px_-16px_rgba(0,0,0,0.28)] motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-4"
        >
          <header className="flex items-center justify-between text-xs text-muted-foreground">
            <span className="flex items-center gap-2">
              <span className="relative flex h-2 w-2">
                <span className="absolute inset-0 rounded-full bg-brand/60 motion-safe:animate-ping" />
                <span className="relative h-2 w-2 rounded-full bg-brand" />
              </span>
              Incoming WhatsApp call
            </span>
            <span aria-label="Ringing for">
              <Elapsed since={ringingSince} />
            </span>
          </header>

          <div className="mt-4 flex items-end gap-2">
            <CallerAvatar name={incoming.customerName} className="h-11 w-11 text-base" />
            <CallBridge moving={!reduceMotion && !ringSilenced} />
            <BusinessEnd />
          </div>

          <div className="mt-3 min-w-0">
            <p className="truncate text-lg font-semibold leading-tight">{incoming.customerName}</p>
            {incoming.waId && (
              <p className="mt-0.5 text-sm tabular-nums text-muted-foreground">
                {formatCallNumber(incoming.waId)}
              </p>
            )}
            {incoming.businessPhone && (
              <p className="mt-1.5 text-xs text-muted-foreground">
                To your number {formatCallNumber(incoming.businessPhone)}
              </p>
            )}
          </div>

          {ringBlocked && !ringSilenced && (
            <p role="note" className="mt-3 rounded-lg bg-muted px-3 py-2 text-xs text-muted-foreground">
              Your browser blocked the ringtone. Click anywhere in Setu to hear it.
            </p>
          )}

          <div className="mt-4 flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="shrink-0 text-muted-foreground"
              onClick={silenceRinging}
              disabled={ringSilenced}
              aria-label={ringSilenced ? 'Silenced' : 'Silence'}
              title={ringSilenced ? 'Ringtone silenced' : 'Silence the ringtone'}
            >
              <BellOff className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="flex-1 text-destructive hover:bg-destructive/10 hover:text-destructive"
              onClick={rejectIncoming}
            >
              <PhoneOff className="mr-2 h-4 w-4" />
              Decline
            </Button>
            <Button className="flex-[1.4]" onClick={() => void acceptIncoming()}>
              <Phone className="mr-2 h-4 w-4" />
              Accept
            </Button>
          </div>
        </section>
      )}

      {active && (
        <section
          role="status"
          aria-label={`Call with ${active.customerName}`}
          className="pointer-events-auto flex items-center gap-3 rounded-2xl border bg-card p-3 text-card-foreground shadow-[0_20px_40px_-16px_rgba(0,0,0,0.25)]"
        >
          <span className="relative flex shrink-0">
            {active.status === 'ongoing' && (
              <span className="absolute -inset-1 rounded-full border-2 border-brand/40 motion-safe:animate-pulse" />
            )}
            <CallerAvatar name={active.customerName} className="relative h-10 w-10 text-sm" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold leading-tight">{active.customerName}</p>
            {active.waId && (
              <p className="truncate text-xs tabular-nums text-muted-foreground">
                {formatCallNumber(active.waId)}
              </p>
            )}
          </div>
          <span
            className={cn(
              'shrink-0 text-sm',
              active.status === 'ongoing' ? 'font-semibold' : 'text-muted-foreground',
            )}
          >
            {active.status === 'ongoing' && active.startedAt ? (
              <Elapsed since={active.startedAt} />
            ) : active.status === 'ringing' ? (
              'Ringing…'
            ) : (
              'Connecting…'
            )}
          </span>
          <div className="flex shrink-0 items-center gap-1">
            {active.customerId && (
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9"
                onClick={() => openChat(active.customerId)}
                aria-label="Open chat"
                title="Open chat"
              >
                <MessageSquare className="h-4 w-4" />
              </Button>
            )}
            <Button
              variant={active.muted ? 'secondary' : 'ghost'}
              size="icon"
              className="h-9 w-9"
              onClick={toggleMute}
              disabled={active.status !== 'ongoing'}
              aria-pressed={active.muted}
              aria-label={active.muted ? 'Unmute' : 'Mute'}
              title={active.muted ? 'Unmute' : 'Mute'}
            >
              {active.muted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
            </Button>
            <Button
              variant="destructive"
              size="icon"
              className="h-9 w-11 rounded-full"
              onClick={hangUp}
              aria-label="Hang up"
              title="Hang up"
            >
              <PhoneOff className="h-4 w-4" />
            </Button>
          </div>
        </section>
      )}
    </div>
  )
}
